// Session synchronization across browser tabs
// Uses BroadcastChannel API when available, falls back to localStorage events

interface SessionMessage {
  type: 'logout' | 'extend' | 'activity'
  timestamp: number
}

export class SessionSync {
  private channel: BroadcastChannel | null = null
  private listeners: Map<string, Set<() => void>> = new Map()
  
  constructor() {
    // Use BroadcastChannel if available
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel('session_sync')
        this.channel.onmessage = (event) => {
          this.handleMessage(event.data)
        }
      } catch (error) {
        console.warn('BroadcastChannel not available:', error)
        this.setupStorageFallback()
      }
    } else {
      this.setupStorageFallback()
    }
  }
  
  /**
   * Setup localStorage fallback for browsers without BroadcastChannel
   */
  private setupStorageFallback() {
    window.addEventListener('storage', (event) => {
      if (event.key === 'session_sync_message' && event.newValue) {
        try {
          const message = JSON.parse(event.newValue)
          this.handleMessage(message)
        } catch (error) {
          console.error('Failed to parse session sync message:', error)
        }
      }
    })
  }
  
  /**
   * Send a message to all tabs
   */
  broadcast(message: SessionMessage) {
    if (this.channel) {
      this.channel.postMessage(message)
    } else {
      // Fallback to localStorage
      localStorage.setItem('session_sync_message', JSON.stringify({
        ...message,
        timestamp: Date.now(),
      }))
      // Clean up after a short delay
      setTimeout(() => {
        localStorage.removeItem('session_sync_message')
      }, 100)
    }
  }
  
  /**
   * Handle incoming messages
   */
  private handleMessage(message: SessionMessage) {
    const listeners = this.listeners.get(message.type)
    if (listeners) {
      listeners.forEach(listener => listener())
    }
  }
  
  /**
   * Listen for specific message types
   */
  on(type: SessionMessage['type'], callback: () => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }
    this.listeners.get(type)!.add(callback)
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(type)
      if (listeners) {
        listeners.delete(callback)
      }
    }
  }
  
  /**
   * Notify all tabs that user logged out
   */
  notifyLogout() {
    this.broadcast({ type: 'logout', timestamp: Date.now() })
  }
  
  /**
   * Notify all tabs that session was extended
   */
  notifyExtend() {
    this.broadcast({ type: 'extend', timestamp: Date.now() })
  }
  
  /**
   * Notify all tabs of user activity
   */
  notifyActivity() {
    this.broadcast({ type: 'activity', timestamp: Date.now() })
  }
  
  /**
   * Clean up resources
   */
  destroy() {
    if (this.channel) {
      this.channel.close()
    }
    this.listeners.clear()
  }
}

// Singleton instance
export const sessionSync = new SessionSync()